import React, { useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileArrowDown, ArrowDown } from "@phosphor-icons/react";

export default function Report() {
  const { t } = useLang();
  const [year, setYear] = useState(new Date().getFullYear());
  const [org, setOrg] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/organization"), api.get("/dashboard", { params: { year } })]).then(([o, d]) => {
      setOrg(o.data);
      setData(d.data);
    });
  }, [year]);

  const download = (path, filename) => {
    const url = `${API_BASE}${path}?year=${year}`;
    const token = localStorage.getItem("cl_token") || "";
    fetch(url, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => r.blob())
      .then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
      });
  };

  if (!data) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex flex-wrap justify-between items-end gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">ISO 14064-1:2018</div>
          <h1 className="font-display text-3xl font-semibold mt-1">Greenhouse Gas Inventory Report</h1>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="w-[120px] font-mono-data" data-testid="report-year">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[0, 1, 2, 3, 4].map((n) => {
                const y = new Date().getFullYear() - n;
                return <SelectItem key={`rep-y-${y}`} value={String(y)}>{y}</SelectItem>;
              })}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => download("/export/csv", `carbon_${year}.csv`)} data-testid="report-csv">
            <ArrowDown size={16} className="mr-2" /> CSV
          </Button>
          <Button onClick={() => download("/export/pdf", `carbon_report_${year}.pdf`)} data-testid="report-pdf">
            <FileArrowDown size={16} className="mr-2" /> PDF
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle className="font-display">1. Reporting Entity</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
          <Field label="Organization" value={org?.name || "—"} />
          <Field label="Reporting Year" value={String(org?.reporting_year || year)} />
          <Field label="Base Year" value={String(org?.base_year || "—")} />
          <Field label="Country / Region" value={org?.country || "—"} />
          <Field label="Industry" value={org?.industry || "—"} />
          <Field label="Consolidation Approach" value="Operational Control" />
          <Field label="Standard" value="ISO 14064-1:2018 / GHG Protocol" />
          <Field label="Generated" value={new Date().toLocaleString()} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="font-display">2. GHG Emissions Summary</CardTitle></CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-muted-foreground border-b border-border">
              <tr>
                <th className="text-left py-2">{t("scope")}</th>
                <th className="text-left py-2">Description</th>
                <th className="text-right py-2">{t("tCO2e")}</th>
                <th className="text-right py-2">%</th>
              </tr>
            </thead>
            <tbody className="font-mono-data">
              {[1, 2, 3].map((s) => {
                const v = data.by_scope[String(s)] || 0;
                const pct = data.total_kg_co2e > 0 ? (v / data.total_kg_co2e) * 100 : 0;
                return (
                  <tr key={s} className="border-b border-border/60">
                    <td className="py-2">Scope {s}</td>
                    <td className="py-2 text-muted-foreground text-xs font-sans">{t(`scopeDescS${s}`).split(".")[0]}</td>
                    <td className="py-2 text-right">{(v / 1000).toFixed(3)}</td>
                    <td className="py-2 text-right">{pct.toFixed(1)}%</td>
                  </tr>
                );
              })}
              <tr className="bg-secondary/50">
                <td colSpan={2} className="py-2 font-semibold font-sans">Total</td>
                <td className="py-2 text-right font-semibold">{data.total_t_co2e.toFixed(3)}</td>
                <td className="py-2 text-right font-semibold">100.0%</td>
              </tr>
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="font-display">3. Methodology</CardTitle></CardHeader>
        <CardContent className="text-sm text-muted-foreground leading-relaxed">
          <p>{t("methodBody")}</p>
          <p className="mt-3">
            The GHG inventory follows ISO 14064-1:2018 principles: relevance, completeness, consistency, accuracy and
            transparency. Emissions are consolidated using the operational control approach.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

const Field = ({ label, value }) => (
  <div>
    <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">{label}</div>
    <div className="mt-0.5">{value}</div>
  </div>
);
